import AsyncStorage from '@react-native-async-storage/async-storage';

import { ScopedAsyncStorage, deviceIdentifier } from '@/lib/storage';

/**
 * Global (non-scoped) AsyncStorage keys that hold per-account state.
 *
 * These keys were created before scoped storage shipped, or are intentionally
 * kept under a single global key for UI persistence. After account deletion
 * they must be cleared so a fresh sign-in (e.g. the same Google account
 * producing a new Supabase user id) starts clean — the calendar onboarding
 * hook is user-scoped, but onboarding only re-triggers in a satisfying way
 * when other per-account state (selectedCalendarIds, view mode, legacy
 * profile keys) is also gone.
 */
const GLOBAL_ACCOUNT_KEYS: readonly string[] = [
  'selectedCalendarIds',
  '@calendar_view_mode',
  '@fimfam/calendar-onboarding-completed',
  '@fimfam/theme-preference',
  '@fimfam/notification-preferences',
  '@fimfam/preferred-name',
  '@fimfam/profile-picture',
  '@fimfam/cover-image',
  '@family_members',
  '@calendar_member_mappings',
  'calendarPreferences',
];

const userScopedOnboardingKey = (userId: string): string =>
  `@fimfam/calendar-onboarding-completed:${userId}`;

/**
 * LocalAccountDataPurger
 *
 * Removes all on-device state belonging to a single account.
 *
 * Invoked from `SupabaseProvider.validateSessionWithServer` when the server
 * rejects the cached JWT — that rejection is how the device learns that the
 * account has been deleted remotely. Purging at that moment guarantees the
 * next sign-in falls into the calendar onboarding flow instead of inheriting
 * the previous account's calendar selection.
 */
export class LocalAccountDataPurger {
  async purge(userId: string | null): Promise<void> {
    await this.purgeGlobalKeys();
    await this.purgeScopedStorage(userId);
    await this.purgeUserScopedOnboarding(userId);
  }

  private async purgeGlobalKeys(): Promise<void> {
    try {
      await AsyncStorage.multiRemove([...GLOBAL_ACCOUNT_KEYS]);
    } catch (error) {
      console.warn(
        '[LocalAccountDataPurger] Failed to remove global account keys:',
        error,
      );
    }
  }

  private async purgeScopedStorage(userId: string | null): Promise<void> {
    if (!userId) {
      return;
    }
    try {
      const deviceId = await deviceIdentifier.getDeviceId();
      const scoped = new ScopedAsyncStorage(userId, deviceId);
      await scoped.clearScope();
    } catch (error) {
      console.warn(
        '[LocalAccountDataPurger] Failed to clear scoped storage:',
        error,
      );
    }
  }

  private async purgeUserScopedOnboarding(userId: string | null): Promise<void> {
    if (!userId) {
      return;
    }
    try {
      await AsyncStorage.removeItem(userScopedOnboardingKey(userId));
    } catch (error) {
      console.warn(
        '[LocalAccountDataPurger] Failed to remove user-scoped onboarding flag:',
        error,
      );
    }
  }
}

export const localAccountDataPurger = new LocalAccountDataPurger();
